<template>
  <div class="mt-4">
    <AppWithLoading v-slot:default="{ loader }" :fetch="fetchUsersWithCountries">
      <FeatureAlertCard
        v-if="account && !account.multiuserMode"
        class="mb-4"
      >
        <template v-slot:title>
          {{ $t('trialTitle') }}
        </template>
        <template v-slot:subtitle>
          <div v-if="account.trialAvailable && !isExpired">
            {{ $t('trialText') }}
          </div>
          <div v-else>
            {{ $t('trialTextIfTrialAvailable') }}
          </div>
        </template>
      </FeatureAlertCard>

      <VBtn class="mb-4" type="success" @click="openDialog('invite')">
        {{ $t('inviteNewUser') }}
      </VBtn>
      <VDataTable
        :key="tableKey"
        :headers="headers"
        :items="tableData"
        hide-default-footer
      >
        <template v-slot:item.actions="{ item }">
          <VIcon
            small
            class="mr-2"
            @click="
              item.isCurrentUser
                ? $router.push('/settings/account')
                : loader(() => openDialog('edit', item))
            "
          >
            mdi-pencil
          </VIcon>
          <VIcon
            v-if="currentUser.id !== item.id && !item.isSuperUser"
            small
            @click="loader(() => openDeleteDialog(item))"
          >
            mdi-delete
          </VIcon>
        </template>
      </VDataTable>
      <VDialog v-if="dialogTariffPlan" persistent max-width="500">
        <VCard>
          <DialogTariffChange :title="dialogHeader" @close="cancel" />
          <template v-slot:action="{ attrs }">
            <VBtn
              color="blue"
              text
              v-bind="attrs"
              @click="closeSnackbarCreated"
            >
              {{ $t('ok') }}
            </VBtn>
          </template>
        </VCard>
      </VDialog>
      <VDialog
        v-if="dialog && account"
        :value="dialog"
        persistent
        max-width="500"
      >
        <VCard class="pa-8">
          <template v-if="showCreateDialog">
            <div class="d-flex pb-5">
              <div class="dialog-header-title">
                {{ dialogHeader }}
              </div>
              <VBtn
                class="v-btn-close"
                icon
                small
                absolute
                top
                right
                @click="cancel"
              >
                <IconClose />
              </VBtn>
            </div>
            <VForm class="popup__form" autocomplete="off" @submit.prevent>
              <VTextField
                v-if="dialogType === 'edit'"
                id="firstName"
                v-model="dialogData.firstName"
                outlined
                dense
                autocomplete="given-name"
                :error-messages="errors.firstName"
              >
                <template v-slot:label>
                  {{ $t('firstName') }} *
                </template>
              </VTextField>
              <VTextField
                v-if="dialogType === 'edit'"
                id="lastName"
                v-model="dialogData.lastName"
                outlined
                dense
                autocomplete="given-name"
                :error-messages="errors.lastName"
              >
                <template v-slot:label>
                  {{ $t('lastName') }} *
                </template>
              </VTextField>
              <VTextField
                :disabled="dialogType === 'edit'"
                id="email"
                v-model="dialogCreateData.email"
                outlined
                dense
                autocomplete="given-name"
                @input="clearErrors"
                :error-messages="errors.email"
              >
                <template v-slot:label>
                  {{ $t('email') }} *
                </template>
              </VTextField>
              <PhoneNumberInput
                v-if="dialogType === 'edit'"
                v-model="dialogData.phone"
                :key="country ? country.alpha2Code : 'RU'"
                :default-country="country ? country.alpha2Code : null"
                :auto-default-country="false"
                required
                :error-messages="errors['phone']"
                @input="errors['phone'] = null"
              />
              <VTextField
                v-if="dialogType === 'edit'"
                id="position"
                v-model="dialogData.position"
                outlined
                dense
                autocomplete="given-name"
                :error-messages="errors.position"
              >
                <template v-slot:label>
                  {{ $t('position') }} *
                </template>
              </VTextField>
              <v-card-actions class="pa-0 ma-0">
                <VBtn
                  class="py-2 px-12 v-btn__send"
                  color="success"
                  id="inviteBtn"
                  style="width: 49%"
                  :disabled="errors.email || !dialogCreateData.email"
                  @click="loader(() => saveDialog())"
                >
                  {{ dialogType === 'create' ? $t('invite') : $t('save') }}
                </VBtn>
                <VBtn
                  class="ma-2 v-btn__delete"
                  color="success"
                  outlined
                  style="width: 49%"
                  @click="cancel()"
                >
                  {{ $t('cancel') }}
                </VBtn>
              </v-card-actions>
            </VForm>
          </template>
          <template v-else>
            <div class="label" />
            <VBtn
              class="v-btn-close"
              icon
              small
              absolute
              top
              right
              @click="cancel"
            >
              <IconClose />
            </VBtn>
            <p class="message">
              {{ $t('needToPayedPlan') }}
            </p>
            <VBtn
              class="v-btn--center btn-upgradePlan success"
              color="#fff"
              outlined
              @click="loader(() => redirectToUpgradePlan())"
            >
              {{ $t('upgradePlan') }}
            </VBtn>
          </template>
        </VCard>
      </VDialog>
      <VDialog v-if="deleteDialog" :value="deleteDialog" max-width="500">
        <VCard class="pa-8">
          <DialogHeader :title="$t('userDeleteHeader')" @close="cancelDelete" />
          <VCardText class="my-6 mx-0 pl-0 pb-0">
            <span>
              {{ $t('acceptDelete') }} {{ deleteUser.firstName }} {{ deleteUser.lastName }}?
            </span>
          </VCardText>
          <VSelect
            :label="$t('responsibleForVacancies')"
            :items="responsibleUsers"
            item-text="label"
            item-value="id"
            v-model="responsibleForVacanciesId"
          />
          <v-card-actions class="mt-0 px-0 pb-0">
            <VBtn
              :disabled="responsibleForVacanciesId === null"
              class="ml-0 px-12"
              id="inviteBtn"
              color="error"
              @click="acceptDelete()"
            >
              {{ $t('delete') }}
            </VBtn>
            <VBtn
              class="ml-2 px-12"
              color="success"
              outlined
              @click="cancelDelete()"
            >
              {{ $t('cancel') }}
            </VBtn>
          </v-card-actions>
        </VCard>
      </VDialog>
      <VDialog v-model="inviteSentDialog" max-width="500" class="elevation-4">
        <VContainer pa-9 style="background:#ffffff" relative>
          <VBtn
            class="v-btn-close"
            icon
            small
            absolute
            top
            right
            @click="inviteSentDialog = false"
          >
            <IconClose />
          </VBtn>
          <VRow align="center" align-content="center" justify="center">
            <VCol cols="12" align="center">
              <svg
                width="72"
                height="72"
                viewBox="0 0 72 72"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="35.9998"
                  cy="36"
                  r="35.3333"
                  fill="#D2F3E5"
                />
                <circle
                  cx="36.0002"
                  cy="36"
                  r="26"
                  fill="#1FC57F"
                  stroke="#1FC57F"
                  stroke-width="1.33333"
                />
                <path
                  d="M25.3335 38.0952L31.6192 44.3809L47.6192 28.3809"
                  stroke="white"
                  stroke-width="2"
                />
              </svg>
            </VCol>
            <VCardTitle class="pa-3">
              {{ $t('invitationSent') }}
            </VCardTitle>
            <VCol cols="12" align="center" class="pa-0 mt-5">
              <VBtn class="button-ok" @click="inviteSentDialog = false">
                {{ $t('ok') }}
              </VBtn>
            </VCol>
          </VRow>
        </VContainer>
      </VDialog>
    </AppWithLoading>
  </div>
</template>

<script>
import AppWithLoading from '@/components/AppWithLoading';
import useUser from '@/compositions/useUser';
import ValidationMixin from '@/mixins/ValidationMixin';
import PhoneNumberInput from '@/components/Input/PhoneNumberInput';
import { computed, ref } from '@vue/composition-api';
import useValidation from '@/compositions/useValidation';
import useCompany from '@/compositions/useCompany';
import { normalizePhoneNumber } from '@/functions';
import { useI18n } from 'vue-i18n-composable';
import useAccount from '@/compositions/accounting/useAccount';
import { paymentTabs, settingsTabs } from '@/enums';
import IconClose from '@/components/SvgIcons/Close.vue';
import FeatureAlertCard from '@/components/Company/FeatureAlertCard.vue';
import useSubscription from '@/compositions/accounting/useSubscription';
import DialogHeader from '../DialogHeader.vue';
import DialogTariffChange from '../DialogTariffChange.vue';

export default {
  name: 'Users',
  components: {
    AppWithLoading,
    DialogHeader,
    PhoneNumberInput,
    DialogTariffChange,
    IconClose,
    FeatureAlertCard,
  },
  mixins: [ValidationMixin],
  data() {
    return {
      dialog: false,
      dialogType: false,
      dialogHeader: null,
      dialogTariffPlan: null,
      dialogEditUserId: null,
      headers: this.getHeaders(),
      dialogData: {
        firstName: null,
        lastName: null,
        phone: null,
        position: null,
      },
      dialogCreateData: {
        email: null,
      },
      deleteDialog: false,
      inviteSentDialog: false,
      deleteUser: null,
      responsibleForVacanciesId: null,
      snackbarUserCreated: false,
    };
  },
  computed: {
    tableData() {
      if (!this.users) {
        return [];
      }

      return this.users.map((user) => {
        user.fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        return user;
      });
    },
    responsibleUsers() {
      if (!this.tableData) {
        return [];
      }
      return this.tableData
        .filter((user) => {
          return user.id !== this.deleteUser?.id;
        })
        .map((user) => {
          if (user.fullName.trim()) {
            user.label = `${user.fullName}`.trim();
          } else {
            user.label = user.email;
          }
          return user;
        });
    },
  },
  setup(_, { root: { $router } }) {
    const {
      currentUser, users, getUsers, deleteUser, createUser, updateUser,
    } = useUser();
    const { isExpired } = useSubscription();

    const { account, fetchIfNull } = useAccount();
    const tempShowPlanAlert = ref(process.env.VUE_APP_DEV_TOOLS === 'true');

    const { locale, t } = useI18n();
    const tableKey = ref(Date.now());

    const { company, fetch } = useCompany();
    const { withValidationHandling } = useValidation();
    const country = ref(null);

    async function fetchCompanyIfEmpty() {
      if (!company.value) {
        await fetch();
      }
    }

    async function loadCountries() {
      await withValidationHandling(async () => {
        await fetchCompanyIfEmpty();
        country.value = company?.value?.country?.alpha2Code;
      });
    }

    async function deleteItem(item, payload) {
      await deleteUser(item.id, payload);
      await getUsers();
    }

    async function fetchUsersWithCountries() {
      await getUsers();
      await loadCountries();
      await fetchIfNull();
    }

    const multiuserModeAvailable = computed(() => account?.value?.active
      && account?.value?.multiuserMode);

    const showCreateDialog = computed(() => {
      if (tempShowPlanAlert.value) {
        return !!account.value && account?.value?.multiuserMode;
      }

      return multiuserModeAvailable.value;
    });

    fetchIfNull();

    return {
      multiuserModeAvailable,
      users,
      getUsers,
      deleteItem,
      currentUser,
      deleteUser,
      createUser,
      updateUser,
      country,
      fetchUsersWithCountries,
      tableKey,
      locale,
      account,
      tempShowPlanAlert,
      paymentTabs,
      settingsTabs,
      showCreateDialog,
      t,
      isExpired,
    };
  },
  watch: {
    locale() {
      this.headers = this.getHeaders();
      this.tableKey = Date.now();
    },
  },
  methods: {
    closeSnackbarCreated() {
      this.snackbarUserCreated = false;
    },
    openDialog(dialogType, item = null) {
      if (dialogType === 'create') {
        this.dialog = true;
        this.dialogType = dialogType;
        this.dialogHeader = this.$t('createDialogHeader');
      }

      if (dialogType === 'invite') {
        this.dialog = true;
        this.dialogType = 'create';
        this.dialogHeader = this.$t('inviteUser');
      }

      if (dialogType === 'edit') {
        this.dialogEditUserId = item.id;
        this.dialogCreateData.email = item.email;
        this.dialogData.phone = item.phone;
        this.dialogData.firstName = item.firstName;
        this.dialogData.lastName = item.lastName;
        this.dialogData.position = item.position;

        this.dialog = true;
        this.dialogType = dialogType;
        this.dialogHeader = this.$t('editDialogHeader', {
          fullName: `${item.firstName || ''} ${item.lastName || ''}`.trim(),
        });
      }
    },
    cancel() {
      this.dialog = false;
      this.dialogHeader = null;
      this.dialogType = null;
      this.dialogData.firstName = null;
      this.dialogData.lastName = null;
      this.dialogCreateData.email = null;
      this.dialogData.phone = null;
      this.dialogData.position = null;
      this.errors = {};
      this.dialogEditUserId = null;
    },
    clearErrors() {
      this.errors = {};
    },
    async redirectToUpgradePlan() {
      this.cancel();
      await this.$router.push({
        name: 'settings',
        params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS },
      });
    },
    async saveDialog() {
      await this.withValidationHandling(async () => {
        switch (this.dialogType) {
          case 'create': {
            const payload = {
              email: (this.dialogCreateData.email || '').toLowerCase(),
            };
            await this.createUser(payload);
            this.inviteSentDialog = true;
            break;
          }
          case 'edit': {
            await this.updateUser(this.dialogEditUserId, {
              firstName: this.dialogData.firstName,
              lastName: this.dialogData.lastName,
              phone: normalizePhoneNumber(this.dialogData.phone),
              position: this.dialogData.position,
            });
            break;
          }
          default: {
            break;
          }
        }
        // const needSnackBarCreated = this.dialogType === 'create';
        const needSnackBarCreated = false;
        this.cancel();
        await this.getUsers();
        if (needSnackBarCreated) {
          this.snackbarUserCreated = true;

          setTimeout(() => {
            this.snackbarUserCreated = false;
          }, 15 * 1000);
        }
      });
    },
    openDeleteDialog(item) {
      this.deleteUser = item;
      this.deleteDialog = true;
    },
    async acceptDelete() {
      await this.deleteItem(this.deleteUser, {
        responsibleForVacancies: this.responsibleForVacanciesId,
      });
      this.cancelDelete();
    },
    cancelDelete() {
      this.deleteDialog = false;
      this.deleteUser = null;
      this.responsibleForVacanciesId = null;
    },
    getHeaders() {
      return [
        {
          text: this.$t('user'),
          align: 'start',
          sortable: false,
          value: 'fullName',
        },
        { text: this.$t('email'), value: 'email' },
        { text: this.$t('phone'), value: 'phone' },
        { text: this.$t('position'), value: 'position' },
        { text: this.$t('actions'), value: 'actions', sortable: false },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.vbtn {
  &__btn-green {
    border: 1px solid #1fc57f;
    color: #1fc57f;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
  }
  &__btn-gray {
    background: #757575;
    color: white;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
  }
}

.v-btn {
  &__delete,
  &__send {
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
  }

  &--center {
    margin: 30px auto 0;
    display: block;
  }
}

.label {
  position: relative;
  margin: 10px auto 0;
  width: 70px;
  height: 70px;
  background-color: #ff0000;
  background-clip: content-box;
  border: 8px solid #ff000060;
  border-radius: 50% !important;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 22px;
    height: 3px;
    background-color: #ffffff;
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
}

.v-card__title {
  font-size: 23px;
  @media screen and (max-width: 427px) {
    text-align: center;
  }
}

.message {
  margin-top: 25px;
  font-size: 24px;
  font-weight: 400;
  line-height: 120%;
  text-align: center;
}
//.popup__form {
//  padding: 0px 18px 30px 18px;
//  .row {
//    margin: 0;
//  }
//}

.button-ok {
  padding: 10px 54px !important;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: #ffffff;
  background: #1fc57f !important;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.24);
  border-radius: 4px;
}

.dialog-header-title {
  font-size: 21px;
  line-height: 21px;
}
</style>

<i18n locale="en" lang="yaml">
inviteNewUser: Invite new user
inviteUser: Invite user
user: User
email: Email
phone: Phone
position: Position
actions: Actions
createDialogHeader: Create user
editDialogHeader: Change user {fullName}
firstName: First name
lastName: Last name
save: Save
invite: Invite
cancel: Cancel
acceptDelete: Are you sure you want to delete the user
delete: Delete
userDeleteHeader: Deleting a user
responsibleForVacancies: Responsible for jobs
userCreated: An invitation email has been sent to the user.
ok: Ok
invitationSent: Invitation sent
needToPayedPlan: Multi-user mode is not available on your plan
upgradePlan: Upgrade plan
toUpgradePlan: Upgrade plan.
trialTitle: The multi-user account is available at the "Standard" tariff and higher
trialText: Get to know all the features of Huntica by activating a 30-day trial period for free
trialTextIfTrialAvailable: Get to know all the features of Huntica
</i18n>

<i18n locale="ru" lang="yaml">
inviteNewUser: Пригласить нового пользователя
inviteUser: Пригласить пользователя
user: Пользователь
email: Email
phone: Номер телефона
position: Должность
actions: Действия
createDialogHeader: Создать пользователя
editDialogHeader: Изменить пользователя {fullName}
firstName: Имя
lastName: Фамилия
save: Сохранить
invite: Пригласить
cancel: Отмена
acceptDelete: Вы уверены что хотите удалить пользователя
delete: Удалить
userDeleteHeader: Удаление пользователя
responsibleForVacancies: Ответственный за вакансии
userCreated: Пользователю отправлено письмо с приглашением.
ok: Ок
invitationSent: Приглашение отправлено
needToPayedPlan: Многопользовательский режим недоступен на вашем тарифе
upgradePlan: Повысить тариф
toUpgradePlan: Повысить тариф.
trialTitle: Многопользовательский кабинет доступен на тарифе «Стандарт» и выше
trialText: Познакомьтесь со всеми возможностями Huntica, активируя пробный период на 30 дней бесплатно
trialTextIfTrialAvailable: Познакомьтесь со всеми возможностями Huntica
</i18n>
